import { Component, Input, OnInit, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ConnectionPositionPair } from '@angular/cdk/overlay';

import { select, Store } from '@ngrx/store';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { environment } from '../../../../environments/environment';
import { CompanyService } from '../../../core/company/company.service';
import { WebHelperService } from '../../services/web-helper/web-helper.service';
import { getUserRole, USER_ROLE } from '../../helpers/roles';
import { ILang } from '../../../ngrx/locale/states/locale.state';
import { IAppState } from '../../../ngrx';
import { Company } from '../../../core/company/company.model';
import { User, UserType } from '../../models/user/user.model';
import { BaseComponent } from '../base/base.component';
import { GeneralNotifications } from '../../models/notification/notification.model';
import { AsyncPipe, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { LanguageChoiserComponent } from '../language-choiser/language-choiser.component';
import { ButtonComponent } from '../../../ui/button/button.component';
import { DropdownItemComponent } from '../../../ui/dropdown-item/dropdown-item.component';
import { MenuComponent } from '../../../ui/menu/menu.component';
import { ProfileImageComponent } from '../../../ui/profile-image/profile-image.component';
import { IconComponent } from '../../../ui/icon/icon.component';
import { MobileMenuComponent } from '../../../ui/mobile-menu/mobile-menu.component';
import { NavigationService } from '../../services/navigation/navigation.service';
import { transitionAnimation } from '../../../../scss/themes/animation';
import { UserService } from '../../../ngrx/user/services/user.service';
import { IndustriesMenuComponent } from '../../../website/industries-menu/industries-menu.component';

type IMainNavComponentTypes = 'website' | 'application';

@Component({
    selector: 'app-main-nav',
    templateUrl: './main-nav.component.html',
    styleUrls: [
        './../../../website/website-grid.scss',
        './main-nav.component.scss',
        './../../../website/website-animations.scss',
    ],
  imports: [
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    LanguageChoiserComponent,
    TranslateModule,
    AsyncPipe,
    NgOptimizedImage,
    ButtonComponent,
    DropdownItemComponent,
    MenuComponent,
    ProfileImageComponent,
    IconComponent,
    IndustriesMenuComponent
],
    animations: [transitionAnimation]
})
export class MainNavComponent extends BaseComponent implements OnInit {
  public homeUrl: string = environment.homeUrl;
  @Input() type: IMainNavComponentTypes = 'application';
  @Input() noAuth = false; // Disable unlogged user bar

  public userType: UserType;
  public user: User;
  public company: Company;
  public companySearchName = '';
  public languages: ILang[] = [];
  public activeLanguage: string;

  public USER_ROLE = USER_ROLE;
  public getUserRole = getUserRole;
  notifications$: Observable<GeneralNotifications>;

  public hasAdvancedTemplateOnly = false;

  positions: ConnectionPositionPair[] = [
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'top'
    },
  ];

  nestedPositions: ConnectionPositionPair[] = [
    new ConnectionPositionPair(
      { originX: 'end', originY: 'top' },  // Origin is the right edge of the trigger element
      { overlayX: 'start', overlayY: 'top' } // Overlay attaches to the left edge of the submenu
    ),
    new ConnectionPositionPair(
      { originX: 'start', originY: 'top' }, // Origin is the left edge of the trigger element
      { overlayX: 'end', overlayY: 'top' } // Overlay attaches to the right edge of the submenu
    )
  ];

  isIndustriesMenuOpen = false;

  deviceWidth: number;

  get isMobile(): boolean {
    return this.deviceWidth <= 1300;
  }

  constructor(
    private router: Router,
    public webHelper: WebHelperService,
    private store: Store<IAppState>,
    private companyService: CompanyService,
    private navigationService: NavigationService,
    private userService: UserService
  ) {
    super();
  }

  ngOnInit(): void {
    this.languages = environment.languages as ILang[];

    this._initUser();
    this._initCompany();

    this.store
      .pipe(
        select((s) => s.ui.locale),
        takeUntil(this.destroy$),
      )
      .subscribe((lang: string) => {
        this.activeLanguage = lang;
      });

    this.store.select(s => s.ui.device).pipe(
      takeUntil(this.destroy$))
      .subscribe((device) => {
        this.deviceWidth = device.screenW;
      }
    );

    // hide industries menu on route change
    this.router.events.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isIndustriesMenuOpen = false;
    });
  }

  public selectLanguage(lang: ILang): void {
    this.webHelper.changeLanguage(lang.code);
  }

  private _initUser(): void {
    this.store
      .select((s) => s.user.current)
      .pipe(
        takeUntil(this.destroy$),
        tap((_) => this._initNotifications()),
      )
      .subscribe((user) => {
        if (!user) {
          this.user = null;
          this.userType = null;
          return;
        }
        this.user = user;
        this.userType = this.userService.isUserEmployee(user) ? 'employee' : 'applicant';

        if (user.user_profile.company) {
          this.companySearchName = user.user_profile.company.search_name;
        }
      });
  }

  private _initCompany(): void {
    this.store
      .pipe(
        select((s) => s.company.current),
        filter((_) => !!_),
        takeUntil(this.destroy$),
      )
      .subscribe((company) => {
        this.company = company;
        this.hasAdvancedTemplateOnly = this.companyService.hasAdvancedViewOnly(company);
      });
  }

  private _initNotifications(): void {
    this.notifications$ = this.store
      .select((s) => s.notification.total)
      .pipe(
        filter((_) => !!_),
        takeUntil(this.destroy$),
      );
  }

  showMobileMenu(
    type: 'website' | 'application' = 'application',
  ): void {

    if (type === 'application') {
      this.navigationService.attachOverlay(MobileMenuComponent, {
        companySearchName: this.companySearchName,
        user: this.user,
        languages: this.languages,
        notifications$: this.notifications$,
        noAuth: this.noAuth,
        hasAdvancedTemplateOnly: this.hasAdvancedTemplateOnly,
        userType: this.userType
      });
    } else if (type === 'website') {
      this.navigationService.attachOverlay(MobileMenuComponent, {
        type: 'website',
        languages: this.languages
      });
    }

  }

  navigateTo(path: string): void {
    this.router.navigate([path]);
  }

  toggleIndustriesMenu(): void {
    this.isIndustriesMenuOpen = !this.isIndustriesMenuOpen;
  }
}
